import { arrayToMap } from 'nucleus-widgets/utils/arrayToMap';
import { mapValues, flow, omit, filter, isEmpty } from 'lodash';
import { getIn, updateIn } from 'icepick';
import * as fromEvent from '../redux/selectors/event';
import GuestInformationStandardContactFields
  from '../migrations/eventData/guestInformationStandardContactFields.json';
import { getRegistrationPathIdForWidget } from
  '../redux/selectors/website/pluginData/registrationProcessNavigation';
import * as RegistrationFieldPageType from './registrationFieldPageType';
import { AudienceType } from './AudienceType';

/*
 * Checks if the registration field exists. It is possible for a contact custom field
 * to be added to an event and later removed from the account. These fields are no longer
 * displayed in the event.
 */
const registrationFieldExists = (contactCustomFields) => (registrationField) => {
  return !registrationField.isCustomField || contactCustomFields[registrationField.fieldId];
};

const setAudienceTypePerRegistrationPathId = (website, question) => {
  if (!question.audienceTypePerRegistrationPathId) {
    let audienceTypePerRegistrationPathId = {};
    const questionItems = filter(website.layoutItems,
      item => item.config && item.config.id === question.question.id);
    Object.values(questionItems).forEach(questionItem => {
      const registrationPathId = getRegistrationPathIdForWidget({ website }, questionItem.id);
      audienceTypePerRegistrationPathId[registrationPathId] = {
        audienceType: questionItem.config.registrationFieldPageType ===
          RegistrationFieldPageType.GuestRegistration ? AudienceType.GUEST_ONLY : AudienceType.INVITEE_ONLY
      };
    });
    return audienceTypePerRegistrationPathId;
  }
  return question.audienceTypePerRegistrationPathId;
};

const updateFieldsInQuestions = (isProductQuestion, productIds, website, registrationPaths) => (eventQuestion) => {
  let updatedQuestion = updateIn(eventQuestion, ['question'],
    question => {
      return {
        ...question,
        oldId: question.id,
        isProductQuestion
      };
    });
  let updatedRegistrationPathQuestionAssociations = updatedQuestion.registrationPathQuestionAssociations;
  if (isEmpty(updatedRegistrationPathQuestionAssociations) ||
    (updatedRegistrationPathQuestionAssociations.length === 1 &&
    (updatedRegistrationPathQuestionAssociations === ['FFFFFFFF-FFFF-FFFF-FFFF-FFFFFFFFFFFF'] ||
    !registrationPaths.find(regPath => regPath.id === updatedRegistrationPathQuestionAssociations[0])))) {
    // For older events with hidden question on all paths setting the reg path association correctly
    // after PROD-69570
    // when reg path is deleted which was the only association to the event question then
    // set the association to default reg path id since having empty association is no more a valid scenario
    updatedRegistrationPathQuestionAssociations = updatedQuestion.hiddenRegistrationPathQuestionAssociations
      && updatedQuestion.hiddenRegistrationPathQuestionAssociations.length > 0 ?
      [updatedQuestion.hiddenRegistrationPathQuestionAssociations[0]] :
      [registrationPaths.find(regPath => regPath.isDefault).id];
    updatedQuestion = updateIn(updatedQuestion, ['question', 'additionalInfo'],
      additionalInfo => {
        return {
          ...additionalInfo,
          active: false
        };
      });
  }

  updatedQuestion = {
    ...updatedQuestion,
    registrationPathQuestionAssociations: [...updatedRegistrationPathQuestionAssociations],
    audienceTypePerRegistrationPathId: setAudienceTypePerRegistrationPathId(website, updatedQuestion)
  };
  if (isProductQuestion) {
    const associatedProductIds = updatedQuestion.productQuestionAssociations;
    return {
      ...updatedQuestion,
      productQuestionAssociations: associatedProductIds.filter(id => productIds.includes(id))
    };
  }
  return updatedQuestion;
};

const removeInvalidQuestionFields = (eventQuestion) => {
  if (getIn(eventQuestion, ['question', 'questionTypeInfo', 'displayType']) === 'List') {
    return updateIn(eventQuestion, ['question', 'questionTypeInfo'],
      questionTypeInfo => omit(questionTypeInfo, ['otherAnswer']));
  }
  return eventQuestion;
};

const transformEventQuestion = (isProductQuestion, productIds, website, registrationPaths) => (eventQuestions) => {
  return flow(
    updateFieldsInQuestions(isProductQuestion, productIds, website, registrationPaths),
    removeInvalidQuestionFields
  )(eventQuestions);
};

const transformEventQuestions = (eventQuestions, isProductQuestion, website, registrationPaths, eventSnapshot) => {
  let productIds = [];
  if (isProductQuestion) {
    const { products: { admissionItems } } = eventSnapshot;
    productIds.push(...Object.keys(admissionItems));
    productIds.push(...fromEvent.getSessions(eventSnapshot).map(session => session.id));
  }
  return mapValues(eventQuestions, transformEventQuestion(isProductQuestion, productIds, website, registrationPaths));
};

const maybeGuestContactFields = (registrationFieldPageType) =>
  registrationFieldPageType === 4
    ? GuestInformationStandardContactFields
    : [];

export const transformRegistrationPath = (account, sharedConfigs = {}) => (registrationPath) => {
  const registrationFieldExistsInAccount = registrationFieldExists(account.contactCustomFields);
  const registrationTypeSettings = registrationPath.registrationTypeSettings ||
      (sharedConfigs && sharedConfigs.RegistrationType) || { limitVisibility: false };
  return {
    ...registrationPath,
    registrationPageFields: arrayToMap(registrationPath.registrationPageFields,
      pageFields => pageFields.registrationFieldPageType,
      pageFields => {
        return {
          ...pageFields,
          registrationFields: {
            ...maybeGuestContactFields(pageFields.registrationFieldPageType),
            ...arrayToMap(
              pageFields.registrationFields.filter(registrationFieldExistsInAccount),
              field => field.fieldId
            )
          }
        };
      }),
    modification: {
      ...registrationPath.modification,
      deadline: registrationPath.modification.deadline && new Date(registrationPath.modification.deadline)
    },
    registrationTypeSettings
  };
};

/*
 * Transforms the event data to make it easier to work with in the application.
 * This primarily changes arrays into maps.
 */
export default function transformEventDataToAppData(eventData, account, event, website) {
  const sharedConfigs = website && website.siteInfo && website.siteInfo.sharedConfigs || {};
  return {
    ...eventData,
    registrationSettings: {
      ...eventData.registrationSettings,
      registrationQuestions: transformEventQuestions(
        eventData.registrationSettings.registrationQuestions, false, website,
        eventData.registrationSettings.registrationPaths),
      productQuestions: transformEventQuestions(eventData.registrationSettings.productQuestions, true, website,
        eventData.registrationSettings.registrationPaths, event),
      registrationPaths: arrayToMap(eventData.registrationSettings.registrationPaths,
        regPath => regPath.id,
        transformRegistrationPath(account, sharedConfigs, eventData, website, event))
    }
  };
}
